import InformationTip from '@/components/InformationTip';
import { Loadable } from '@/components/loaders';
import { StepPageActions } from '@/global/components';
import { Checkbox, DownloadIcon, IconButton } from '@/lib';
import { Select } from '@/lib/Selects';
import { Header } from '@/models/dataSource';
import { useGetTraitsQuery } from '@/services/endpoints/traits';
import { generateSlug } from '@/utils';
import { Form, Formik, FormikProps } from 'formik';
import { FC, RefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRAITS } from '../../constants';
import { ColumnItem, ColumnMapSelect } from '../../types';
import { DataTypeInput, TraitInput } from '../dataSourceTypes/file/commons/input';
import { useColumnMapInitialValues } from '../dataSourceTypes/file/hooks/useColumnMapInitialValues';

const NORMALIZATION_GUIDE_URL = 'https://download.audiencerate.dev/mdp/Normalizzazione_Indirizzi.pdf';

export type ColumnMapEditorProps = {
  workspaceId: string;
  headers: string[];
  previewRows: string[][];
  formRef: RefObject<FormikProps<ColumnItem[]>>;
  onConfirm: (mappings: Header[], options?: { normalizeAddresses?: boolean }) => void | Promise<void>;
  onPrev?: () => void;
  isConfirmDisabled?: boolean;
  showAddressNormalization?: boolean;
  confirmTooltipEnabled?: boolean;
  confirmTooltipContent?: string;
};

export const buildPreviewRowsFromFieldRecords = (headers: string[], records: Record<string, unknown>[], maxRows = 4): string[][] => {
  return records.slice(0, maxRows).map((record) => headers.map((header) => String(record[header] ?? '')));
};

export const getHeadersFromColumnMap = (columnMap: ColumnItem[]): Header[] => {
  return columnMap.map((column) => {
    if (column.trait.id !== TRAITS.IGNORE_COLUMN) {
      return {
        name: column.header,
        traitId: column.traitValue?.id || '',
        displayName: column.traitValue?.displayValue || '',
        custom: column.trait.id === TRAITS.CUSTOM_TRAIT,
        customDataType: column.customDataType,
        skip: false,
      };
    }
    return {
      name: column.header,
      skip: column.trait.id === TRAITS.IGNORE_COLUMN,
    };
  });
};

export const ColumnMapEditor: FC<ColumnMapEditorProps> = ({
  workspaceId,
  headers,
  previewRows,
  formRef,
  onConfirm,
  onPrev,
  isConfirmDisabled = false,
  showAddressNormalization = false,
  confirmTooltipEnabled = false,
  confirmTooltipContent,
}) => {
  const { t } = useTranslation('data_source_edit');
  const normalizeTooltipContent = t('file.map_column.normalize_tooltip_content');
  const normalizeMinRequirementsMessage = t('file.map_column.normalize_minimum_requirements_message');
  const [normalizeAddresses, setNormalizeAddresses] = useState(false);

  const { data: groupedTraits, isLoading: isLoadingTraits } = useGetTraitsQuery({ workspaceId });

  const traitDotNamesById = useMemo(() => {
    const map = new Map<string | number, string>();
    if (groupedTraits) {
      for (const trait of [...groupedTraits.customer, ...groupedTraits.event]) {
        map.set(trait.id, trait.dotName);
      }
    }
    return map;
  }, [groupedTraits]);

  const columnMapSelect: ColumnMapSelect[] = [
    { id: TRAITS.MAP_TO_TRAIT, label: t('file.map_column.map_to_trait') },
    { id: TRAITS.CUSTOM_TRAIT, label: t('file.map_column.custom_trait') },
    { id: TRAITS.CONSENT_TRAIT, label: t('file.map_column.consent_trait') },
    { id: TRAITS.IGNORE_COLUMN, label: t('file.map_column.ignore_column') },
  ];

  const validateForm = (values: ColumnItem[]) => {
    const errors: { [key: number]: string }[] = [];
    values.filter((column, index) => {
      switch (column.trait.id) {
        case TRAITS.MAP_TO_TRAIT:
          if (!column.traitValue) {
            errors[index] = t('file.map_column.validation.select_trait');
          }
          break;
        case TRAITS.CUSTOM_TRAIT:
          if (!column.traitValue || !column.traitValue.id || !column.traitValue.displayValue || !column.customDataType) {
            errors[index] = t('file.map_column.validation.value_cannot_be_blank');
          }
          break;
        case TRAITS.CONSENT_TRAIT:
          if (!column.traitValue) {
            errors[index] = t('file.map_column.validation.value_cannot_be_blank');
          }
          break;
        default:
          break;
      }
    });
    return errors;
  };

  const initialValues = useColumnMapInitialValues({ fileHeader: headers, columnMapSelect, groupedTraits, defaultNonMatchingTraitId: 3 });

  const getMappedDotNames = (columnMap?: ColumnItem[]) => {
    return new Set(
      columnMap?.filter((col) => col.trait.id === TRAITS.MAP_TO_TRAIT && col.traitValue?.id).map((col) => traitDotNamesById.get(col.traitValue!.id))
    );
  };

  const getHasAddressNormalizationRequirements = (columnMap?: ColumnItem[]) => {
    const mappedDotNames = getMappedDotNames(columnMap);
    return mappedDotNames.has('address_street') && mappedDotNames.has('address_city') && mappedDotNames.has('address_postal_code');
  };

  const handleConfirm = async () => {
    await formRef.current?.submitForm();
    if (formRef.current?.isValid && formRef.current?.values) {
      const columnMap: ColumnItem[] = formRef.current.values;
      const hasAddressNormalizationRequirements = getHasAddressNormalizationRequirements(columnMap);
      const shouldNormalize = showAddressNormalization && normalizeAddresses && hasAddressNormalizationRequirements;
      const mappings = getHeadersFromColumnMap(columnMap);
      await onConfirm(mappings, { normalizeAddresses: shouldNormalize });
    }
  };

  const handlePrev = async () => {
    await formRef.current?.submitForm();
    if (formRef.current?.isValid && formRef.current?.values) {
      onPrev?.();
    }
  };

  const handleDownloadNormalizationGuide = () => {
    window.open(NORMALIZATION_GUIDE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Loadable isLoading={isLoadingTraits}>
      <Formik
        onSubmit={(values, { validateForm: validate }) => validate(values)}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
        validate={validateForm}
        innerRef={formRef}
        enableReinitialize
      >
        {({ values, setFieldValue, setFieldError, errors }) => {
          const hasAddressNormalizationRequirements = getHasAddressNormalizationRequirements(values);
          const isPrimaryButtonDisabled =
            isConfirmDisabled || (showAddressNormalization && normalizeAddresses && !hasAddressNormalizationRequirements);

          const updateColumnValue = (index: number, value: ColumnItem) => {
            const nextValues = [...values];
            nextValues[index] = value;

            if (showAddressNormalization && normalizeAddresses && !getHasAddressNormalizationRequirements(nextValues)) {
              setNormalizeAddresses(false);
            }

            setFieldValue(String(index), value);
          };

          return (
            <>
              <Form className='flex flex-col gap-8'>
                {values.map((traitValue, index) => (
                  <div className='flex gap-8' key={traitValue.id}>
                    <div className='w-72 min-w-72 overflow-hidden rounded-xl border'>
                      <div className='bg-secondary-background px-6 py-3'>
                        <p className='text-sm font-semibold text-quaternary-500'>{traitValue.header}</p>
                      </div>
                      {previewRows.map((row, rowIndex) => (
                        <div key={`${traitValue.header}_${rowIndex}`} className='border-t p-6 text-sm text-tertiary-600'>
                          {row[index] ?? ''}
                        </div>
                      ))}
                    </div>
                    <Select
                      items={columnMapSelect}
                      value={traitValue.trait}
                      onChange={(selectItemValue) => {
                        setFieldError(String(index), undefined);
                        if (selectItemValue.id == TRAITS.CUSTOM_TRAIT) {
                          updateColumnValue(index, {
                            ...traitValue,
                            trait: selectItemValue,
                            traitValue: {
                              id: generateSlug(traitValue.header),
                              displayValue: traitValue.header,
                            },
                          });
                        } else {
                          updateColumnValue(index, { ...traitValue, trait: selectItemValue, traitValue: undefined });
                        }
                      }}
                    />
                    <div className='min-w-48 max-w-64'>
                      <TraitInput
                        option={traitValue.trait}
                        error={errors[index] as string}
                        value={traitValue.traitValue}
                        onChange={(value) => {
                          setFieldError(String(index), undefined);
                          updateColumnValue(index, { ...traitValue, traitValue: value });
                        }}
                      />
                    </div>
                    <DataTypeInput
                      option={traitValue.trait}
                      error={errors[index] as string}
                      value={traitValue.customDataType}
                      onChange={(value) => {
                        setFieldError(String(index), undefined);
                        setFieldValue(String(index), { ...traitValue, customDataType: value });
                      }}
                    />
                  </div>
                ))}

                {showAddressNormalization && (
                  <>
                    <div className='mt-4 flex gap-2'>
                      <Checkbox
                        label={t('file.map_column.normalize_addresses')}
                        disabled={!hasAddressNormalizationRequirements}
                        checked={normalizeAddresses && hasAddressNormalizationRequirements}
                        classNames={{
                          label: hasAddressNormalizationRequirements ? 'text-quaternary-700' : 'text-quaternary-400',
                        }}
                        onChange={(e) => {
                          if (!hasAddressNormalizationRequirements) {
                            setNormalizeAddresses(false);
                            return;
                          }
                          setNormalizeAddresses(e.target.checked);
                        }}
                      />
                      <InformationTip tooltipContent={normalizeTooltipContent} />
                      <IconButton
                        intent='secondary'
                        size='sm'
                        leadingIcon={<DownloadIcon />}
                        text={t('file.map_column.download_normalization_guide')}
                        onClick={handleDownloadNormalizationGuide}
                      />
                    </div>
                    <div className='mt-4 w-fit bg-secondary-background px-6 py-3'>{normalizeMinRequirementsMessage}</div>
                  </>
                )}
              </Form>

              <StepPageActions
                primaryButton={{
                  text: 'COMMON.confirm',
                  onClick: handleConfirm,
                  disabled: isPrimaryButtonDisabled,
                }}
                secondaryButton={{
                  text: 'COMMON.cancel',
                  onClick: handlePrev,
                }}
                isPrimaryButtonTooltipEnabled={confirmTooltipEnabled}
                tooltipContent={confirmTooltipContent}
              />
            </>
          );
        }}
      </Formik>
    </Loadable>
  );
};
